/**
 * Tag Migration Utility
 * Helps migrate descriptions that should be tags into the tags array
 */

import { WorkItem } from '../types';

/**
 * Analyzes description field and suggests potential tags
 */
export const extractPotentialTags = (description: string): string[] => {
  if (!description) return [];
  
  const potentialTags: string[] = [];
  
  // Common patterns that might be tags
  // 1. Short descriptions (less than 3 words)
  const words = description.trim().split(/\s+/);
  if (words.length <= 3 && description.length <= 30) {
    potentialTags.push(description.trim());
  }
  
  // 2. Course-related patterns
  if (description.toLowerCase().includes('course') || 
      description.toLowerCase().includes('learning') ||
      description.toLowerCase().includes('tutorial')) {
    potentialTags.push(description.trim());
  }
  
  // 3. Status-like descriptions
  const statusPatterns = ['todo', 'in progress', 'done', 'review', 'pending', 'completed'];
  if (statusPatterns.some(pattern => description.toLowerCase().includes(pattern))) {
    potentialTags.push(description.trim());
  }
  
  return potentialTags;
};

/**
 * Migrate appropriate descriptions to tags
 */
export const migrateDescriptionsToTags = () => {
  try {
    const storedData = localStorage.getItem('cleanRoomData');
    if (!storedData) return;
    
    const items: WorkItem[] = JSON.parse(storedData);
    let migrationCount = 0;
    
    console.log('Starting tag migration...');
    console.log('Total items to check:', items.length);
    
    const updatedItems = items.map(item => {
      // Only migrate very short descriptions that look like tags
      if (item.description && item.description.trim()) {
        const desc = item.description.trim();
        const descWords = desc.split(/\s+/).length;
        
        // If description is 1-3 words and under 30 chars, it's likely a tag
        if (descWords <= 3 && desc.length <= 30) {
          const currentTags = item.tags || [];
          
          // Don't migrate if it's already in tags
          if (!currentTags.includes(desc)) {
            item.tags = [...currentTags, desc];
            item.description = '';
            migrationCount++;
            console.log(`Migrated "${desc}" from description to tags for item: ${item.fileName}`);
          }
        }
      }
      
      return item;
    });
    
    if (migrationCount > 0) {
      // Save the updated items
      localStorage.setItem('cleanRoomData', JSON.stringify(updatedItems));
      
      // Update saved tags
      const allTags = new Set<string>();
      updatedItems.forEach(item => {
        if (item.tags) {
          item.tags.forEach(tag => allTags.add(tag));
        }
      });
      
      const tagsArray = Array.from(allTags).sort();
      localStorage.setItem('focusHubSavedTags', JSON.stringify(tagsArray));
      
      console.log(`Migration complete: Moved ${migrationCount} descriptions to tags`);
      console.log('All tags now available:', tagsArray);

      // Don't reload - let React handle the state updates naturally
    } else {
      console.log('No descriptions needed migration to tags');
      
      // Still collect and save all existing tags
      const allTags = new Set<string>();
      items.forEach(item => {
        if (item.tags) {
          item.tags.forEach(tag => allTags.add(tag));
        }
      });
      
      if (allTags.size > 0) {
        const tagsArray = Array.from(allTags).sort();
        localStorage.setItem('focusHubSavedTags', JSON.stringify(tagsArray));
        console.log('Collected existing tags:', tagsArray);
      }
    }
  } catch (error) {
    console.error('Error during tag migration:', error);
  }
};

/**
 * Manually add a tag to an item
 */
export const addTagToItem = (itemId: string, tag: string) => {
  try {
    const storedData = localStorage.getItem('cleanRoomData');
    if (!storedData) return false;
    
    const items: WorkItem[] = JSON.parse(storedData);
    const itemIndex = items.findIndex(item => item.id === itemId);
    
    if (itemIndex === -1) return false;
    
    const item = items[itemIndex];
    const currentTags = item.tags || [];
    
    if (!currentTags.includes(tag)) {
      items[itemIndex] = {
        ...item,
        tags: [...currentTags, tag]
      };
      
      localStorage.setItem('cleanRoomData', JSON.stringify(items));
      
      // Update saved tags
      const savedTags = localStorage.getItem('focusHubSavedTags');
      const allTags = savedTags ? JSON.parse(savedTags) : [];
      if (!allTags.includes(tag)) {
        allTags.push(tag);
        localStorage.setItem('focusHubSavedTags', JSON.stringify(allTags));
      }
      
      return true;
    }
    
    return false;
  } catch (error) {
    console.error('Error adding tag to item:', error);
    return false;
  }
};